import { useMemo } from 'react';

import {
  EntKind,
  MapCoordsGranularity,
  POSTINGS_V3_GET_DATA,
  type PostingsV3DataQuery,
  type PostingsV3Ent,
} from '@revelio/data-access';

import { useSelectionListsForKeys } from '../../../../shared/selection-lists/hooks/useSelectionListsForKeys';
import { useFilterContext } from '../../filters/contexts/FilterProvider';
import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import type { PrimaryFilterId } from '../../filters/types/filters';
import { getPostingFilters } from '../../filters/utils/getPostingFilters';
import { buildPostingsV3VariablesFromEntities } from '../../filters/utils/getPostingsV3Variables';
import { useChart } from '../Chart/ChartProvider';
import type { MapPoint } from '../Chart/Map/types';
import type { PostingMetricValues } from '../PageConfigProvider/types/metric';
import { type DataProviderChildren, DataProviderQueryStatus } from './types';
import { convertPostingsV3MapData } from './utils/convert-postings-v3-map-data';
import {
  type GranularityWithIds,
  useCoordinateResolverForMixedGranularities,
  withCoordinateStatus,
} from './utils/coordinate-resolver';
import { useUrqlQueryWithStatus } from './utils/data-provider-results-utils';

type PostingMapDataProviderProps = {
  primaryDimension: PrimaryFilterId;
  metric: PostingMetricValues;
  exchangeRate?: number;
  children: DataProviderChildren<{ points: MapPoint[] } | undefined>;
};

const granularityToEntKind = (granularity: MapCoordsGranularity): EntKind => {
  switch (granularity) {
    case MapCoordsGranularity.Country:
      return EntKind.Country;
    case MapCoordsGranularity.Msa:
      return EntKind.Msa;
    case MapCoordsGranularity.Region:
      return EntKind.Region;
  }
};

export const PostingMapDataProvider = ({
  primaryDimension,
  metric,
  exchangeRate,
  children,
}: PostingMapDataProviderProps) => {
  const { item: _item } = useChart();
  const { activeFilterMap } = useFilterContext();
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'week' });
  const { selectionLists } = useSelectionListsForKeys(['country', 'msa']);
  const { country: countryList, msa: msaList } = selectionLists;
  const postingFilters = getPostingFilters(primaryDimension, effectiveFilters);

  // Geographies to plot: no geo filter → all countries; only countries → those
  // countries + their MSAs; regions/MSAs → as selected. These become
  // `req.entities` of a single request.
  const geoGranularities = useMemo((): GranularityWithIds[] => {
    // Wait for both universe lists — they load together; enriching with a
    // half-loaded universe would request an empty geography set.
    if (!countryList || !msaList) return [];

    // `PostingFilters` geo ids are loosely typed; narrow to numbers.
    const toNumberIds = (
      values: ReadonlyArray<string | number | null> | undefined | null
    ): number[] =>
      (values ?? [])
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value));

    const region = toNumberIds(postingFilters.region);
    const country = toNumberIds(postingFilters.country);
    const msa = toNumberIds(postingFilters.msa);

    if (!region.length && !country.length && !msa.length) {
      return [
        {
          granularity: MapCoordsGranularity.Country,
          ids: countryList.map((c) => Number(c.id)),
        },
      ];
    }

    if (country.length && !msa.length) {
      const enrichedMsa = msaList
        .filter((m) => country.includes(Number(m.country)))
        .map((m) => Number(m.id));
      return [
        { granularity: MapCoordsGranularity.Country, ids: country },
        { granularity: MapCoordsGranularity.Msa, ids: enrichedMsa },
      ];
    }

    const granularities: GranularityWithIds[] = [];
    if (region.length) {
      granularities.push({
        granularity: MapCoordsGranularity.Region,
        ids: region,
      });
    }
    if (country.length) {
      granularities.push({
        granularity: MapCoordsGranularity.Country,
        ids: country,
      });
    }
    if (msa.length) {
      granularities.push({ granularity: MapCoordsGranularity.Msa, ids: msa });
    }
    return granularities;
  }, [postingFilters, countryList, msaList]);

  // One request entity per geography; the converter zips the response back to
  // this list by index to recover each geo's id for the coord lookup.
  const requestEntities = useMemo(
    (): PostingsV3Ent[] =>
      geoGranularities.flatMap((g) =>
        g.ids.map((id) => ({
          EntID: id,
          EntType: granularityToEntKind(g.granularity),
        }))
      ),
    [geoGranularities]
  );

  const variables = useMemo(
    () =>
      buildPostingsV3VariablesFromEntities({
        entities: requestEntities,
        effectiveFilters,
        defaultMetricMode: 'posting',
        activeFilterMap,
        // Pass the prop explicitly so the request's dimension matches the
        // geo-enrichment above instead of relying on entity-kind derivation.
        primaryDimension,
      }),
    [requestEntities, effectiveFilters, activeFilterMap, primaryDimension]
  );

  const [{ data, status }] = useUrqlQueryWithStatus<PostingsV3DataQuery>({
    query: POSTINGS_V3_GET_DATA,
    variables,
    pause: requestEntities.length === 0,
  });

  const { data: coords, ...coordsState } =
    useCoordinateResolverForMixedGranularities(geoGranularities);

  const points = useMemo(
    (): MapPoint[] =>
      convertPostingsV3MapData({
        data,
        requestEntities,
        metric,
        exchangeRate,
        coords,
      }),
    [data, requestEntities, metric, exchangeRate, coords]
  );

  return children({
    data: { points },
    ...withCoordinateStatus(
      status ?? DataProviderQueryStatus.Idle,
      coordsState
    ),
  });
};
